import * as rt from 'runtypes';

/**
 * Runtype validators for each option
 */
const optionValidators = {
    join: rt.String,
    ww: rt.Number,
    l: rt.Number,
    test: rt.Boolean,
    debug: rt.Boolean,
    light: rt.Boolean,
};


export type UrlOptions = Partial<rt.Static<rt.Object<typeof optionValidators>>>;
/**
 * Parse a single URL option value based on different formats
 * @param key - The key part of the option
 * @param value - The value part (may be empty string for flags)
 * @param expectedType - Optional expected type for the key (used to disambiguate)
 * @param hadEquals - Whether the original part had an equals sign
 */
function parseOptionValue(
    key: string, 
    value: string, 
    expectedType?: 'string' | 'number' | 'boolean',
    hadEquals?: boolean
): { key: string; value: string | number | boolean } {
    // Empty value with equals sign means empty string (e.g., "join=")
    if (value === '' && hadEquals) {
        return { key, value: '' };
    }

    // Empty value without equals sign means it's a flag
    if (value === '') {
        return { key, value: true };
    }

    // Boolean formats: true, false, 1, 0
    if (value === 'true' || value === '1') {
        return { key, value: true };
    }
    if (value === 'false' || value === '0') {
        return { key, value: false };
    }

    // Check for single digit 0 or 1 suffix when expecting boolean
    if (expectedType === 'boolean' && (value === '0' || value === '1')) {
        return { key, value: value === '1' };
    }

    // Number format: numeric value
    const numValue = Number(value);
    if (!isNaN(numValue) && value.trim() !== '') {
        return { key, value: numValue };
    }

    // String format: any other value
    return { key, value };
}

/**
 * Parse URL fragment string into options
 * Format: option1,option2=value,option3
 * @param fragment - The fragment string without the leading #
 */
export function parseUrlFragment(fragment: string): UrlOptions {
    if (!fragment) {
        return {};
    }

    const parts = fragment.split(',').map(p => p.trim()).filter(p => p.length > 0);

    const result: UrlOptions = {};

    for (const part of parts) {
        // Find the first non-letter character to separate key from value
        let keyEndIndex = 0;
        for (let i = 0; i < part.length; i++) {
            const char = part[i];
            if (!/[a-zA-Z]/.test(char)) {
                keyEndIndex = i;
                break;
            }
        }

        // If no non-letter found, the whole part is the key
        if (keyEndIndex === 0) {
            keyEndIndex = part.length;
        }

        const key = part.substring(0, keyEndIndex);
        let value = part.substring(keyEndIndex);

        // Check if original value started with '='
        const hadEquals = value.startsWith('=');

        // If value starts with '=', remove it
        if (hadEquals) {
            value = value.substring(1);
        }

        // Skip if key is empty
        if (!key) {
            continue;
        }

        // Try to determine expected type by checking if key is known
        let expectedType: 'string' | 'number' | 'boolean' | undefined;
        if (key in optionValidators) {
            const validator = optionValidators[key as keyof typeof optionValidators];
            if (validator === rt.String) expectedType = 'string';
            else if (validator === rt.Number) expectedType = 'number';
            else if (validator === rt.Boolean) expectedType = 'boolean';
        }

        const parsed = parseOptionValue(key, value, expectedType, hadEquals);

        // Check if this is an expected key and validate its type
        if (parsed.key in optionValidators) {
            const validator = optionValidators[parsed.key as keyof typeof optionValidators];
            if (validator.guard(parsed.value)) {
                // Type assertion is necessary because TypeScript can't infer that the runtime
                // validation guarantees the value type matches the key's type in UrlOptions
                result[parsed.key as keyof UrlOptions] = parsed.value as never;
            }
        }
    }

    return result;
}

/**
 * Parse and validate URL options from the current window location
 */
function parseUrlOptions(): UrlOptions {
    // Check if window is available (for SSR compatibility)
    if (typeof window === 'undefined') {
        return {};
    }

    const hash = window.location.hash;
    if (!hash) {
        return {};
    }

    // Remove leading # and parse
    return parseUrlFragment(hash.substring(1));
}

/**
 * Exported URL options object
 * Parse once at module load time
 */
export const urlOptions: UrlOptions = parseUrlOptions();
